import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  RefreshCw, AlertCircle, ArrowLeft, Copy, Check, Tag, Link,
  QrCode, Plus, Trash2, X, Download, ChevronDown, List, BarChart2, Pencil,
} from 'lucide-react';
import Alert, { AlertDescription } from '../components/ui/alert';
import QRCodeStyling from 'qr-code-styling';

const API_BASE = 'https://api.notely.se';
const DASHBOARD_SECRET = process.env.REACT_APP_DASHBOARD_SECRET;

// ─── Promo Code types ────────────────────────────────────────────────────────

interface PromoCode {
  id: number;
  code: string;
  plan: 'premium_monthly_1' | 'premium_monthly_2';
  duration_months: number;
  max_uses: number;
  used_count: number;
  expires_at: string | null;
  is_active: number;
  created_at: string;
}

const PLAN_LABELS: Record<string, string> = {
  premium_monthly_1: 'Basic',
  premium_monthly_2: 'Standard',
};

function generateRandomCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

// ─── Campaign types ──────────────────────────────────────────────────────────

interface CampaignCode {
  id: number;
  code: string;
  platform: 'ios' | 'android';
  expires_at: string | null;
  is_claimed: number;
  claimed_at: string | null;
}

interface Campaign {
  id: string;
  name: string;
  slug: string | null;
  fallback_url: string;
  ios_redirect_url: string | null;
  android_redirect_url: string | null;
  use_landing_page: boolean;
  primary_button_text: string | null;
  secondary_button_text: string | null;
  is_active: number;
  created_at: string;
  ios_codes: CampaignCode[];
  android_codes: CampaignCode[];
  ios_total: number;
  ios_claimed: number;
  android_total: number;
  android_claimed: number;
  earliest_expires_at: string | null;
}

interface CampaignClick {
  id: number;
  platform: string;
  user_agent: string | null;
  ip_address: string | null;
  is_new_claim: number;
  clicked_at: string;
}

interface ClickTotals {
  platform: string;
  total: number;
  new_claims: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

function parseBrowserFromUA(ua: string | null): string {
  if (!ua) return 'Unknown';
  if (/CriOS/i.test(ua))   return 'Chrome iOS';
  if (/FxiOS/i.test(ua))   return 'Firefox iOS';
  if (/Edg\//i.test(ua))   return 'Edge';
  if (/Chrome/i.test(ua))  return 'Chrome';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Safari/i.test(ua))  return 'Safari';
  return 'Other';
}

function getCampaignValidUntil(campaign: Campaign): string {
  if (!campaign.earliest_expires_at) return '—';
  return new Date(campaign.earliest_expires_at).toLocaleDateString();
}

const PromoCodes: React.FC = () => {
  // Tab
  const [activeTab, setActiveTab] = useState<'codes' | 'campaigns'>(
    () => (localStorage.getItem('promoCodes_activeTab') as 'codes' | 'campaigns') ?? 'codes'
  );

  // ── Promo Codes state ──
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [copiedUrlId, setCopiedUrlId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Promo code form
  const [formCode, setFormCode] = useState('');
  const [formPlan, setFormPlan] = useState<'premium_monthly_1' | 'premium_monthly_2'>('premium_monthly_1');
  const [formDuration, setFormDuration] = useState(1);
  const [formMaxUses, setFormMaxUses] = useState(1);
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Campaigns state ──
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [campaignsSuccess, setCampaignsSuccess] = useState<string | null>(null);

  // Create campaign form
  const [campName, setCampName] = useState('');
  const [campFallbackUrl, setCampFallbackUrl] = useState('https://notely.se/download');
  const [campIosRedirectUrl, setCampIosRedirectUrl] = useState('https://apps.apple.com/redeem?ctx=offercodes&id=6740462619&code={code}');
  const [campAndroidRedirectUrl, setCampAndroidRedirectUrl] = useState('https://play.google.com/redeem?code={code}');
  const [campIosCodes, setCampIosCodes] = useState('');
  const [campAndroidCodes, setCampAndroidCodes] = useState('');
  const [campExpiresAt, setCampExpiresAt] = useState('');
  const [campUseLandingPage, setCampUseLandingPage] = useState(true);
  const [campPrimaryButtonText, setCampPrimaryButtonText] = useState('');
  const [campSecondaryButtonText, setCampSecondaryButtonText] = useState('');
  const [campSubmitting, setCampSubmitting] = useState(false);
  const [campFormError, setCampFormError] = useState<string | null>(null);

  // QR modal
  const [qrCampaign, setQrCampaign] = useState<Campaign | null>(null);
  const [qrUrlCopied, setQrUrlCopied] = useState(false);
  const qrInstanceRef = useRef<QRCodeStyling | null>(null);
  const qrContainerRef = useRef<HTMLDivElement>(null);

  // Add codes modal
  const [addCodesCampaign, setAddCodesCampaign] = useState<Campaign | null>(null);
  const [addIosCodes, setAddIosCodes] = useState('');
  const [addAndroidCodes, setAddAndroidCodes] = useState('');
  const [addExpiresAt, setAddExpiresAt] = useState('');
  const [addCodesSubmitting, setAddCodesSubmitting] = useState(false);
  const [addCodesError, setAddCodesError] = useState<string | null>(null);

  // Expanded campaign rows
  const [expandedCampIds, setExpandedCampIds] = useState<Set<string>>(new Set());
  const toggleCampExpanded = (id: string) =>
    setExpandedCampIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Toggle/delete campaign
  const [togglingCampId, setTogglingCampId] = useState<string | null>(null);
  const [deletingCampId, setDeletingCampId] = useState<string | null>(null);

  // Codes modal
  const [codesCampaign, setCodesCampaign] = useState<Campaign | null>(null);
  const [modalCodes, setModalCodes] = useState<CampaignCode[]>([]);
  const [modalCodesLoading, setModalCodesLoading] = useState(false);
  const [modalCodesError, setModalCodesError] = useState<string | null>(null);
  const [reactivatingCodeId, setReactivatingCodeId] = useState<number | null>(null);

  // Stats modal
  const [statsCampaign, setStatsCampaign] = useState<Campaign | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsTotals, setStatsTotals] = useState<ClickTotals[]>([]);
  const [statsRecent, setStatsRecent] = useState<CampaignClick[]>([]);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Edit modal (URLs + slug + landing page settings)
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  const [editSlug, setEditSlug] = useState('');
  const [editFallbackUrl, setEditFallbackUrl] = useState('');
  const [editIosRedirectUrl, setEditIosRedirectUrl] = useState('');
  const [editAndroidRedirectUrl, setEditAndroidRedirectUrl] = useState('');
  const [editUseLandingPage, setEditUseLandingPage] = useState(true);
  const [editPrimaryButtonText, setEditPrimaryButtonText] = useState('');
  const [editSecondaryButtonText, setEditSecondaryButtonText] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // ── Promo Codes API ──────────────────────────────────────────────────────

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/dashboard/promo-codes`, {
        headers: { 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCodes(data.codes ?? []);
    } catch (e: any) {
      setError(e.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  useEffect(() => {
    if (!qrCampaign) return;
    const qr = new QRCodeStyling({
      width: 160,
      height: 160,
      data: `https://notely.se/c/${qrCampaign.slug || qrCampaign.id}`,
      dotsOptions: { type: 'rounded', color: '#000000' },
      cornersSquareOptions: { type: 'extra-rounded' },
      cornersDotOptions: { type: 'dot' },
      backgroundOptions: { color: '#ffffff' },
      qrOptions: { errorCorrectionLevel: 'H' },
    });
    qrInstanceRef.current = qr;
    if (qrContainerRef.current) {
      qrContainerRef.current.innerHTML = '';
      qr.append(qrContainerRef.current);
    }
  }, [qrCampaign]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!formCode.trim()) { setFormError('Code is required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/promo-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-dashboard-secret': DASHBOARD_SECRET ?? '',
        },
        body: JSON.stringify({
          code: formCode.trim().toUpperCase(),
          plan: formPlan,
          durationMonths: formDuration,
          maxUses: formMaxUses,
          expiresAt: formExpiresAt || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setFormCode(''); setFormPlan('premium_monthly_1'); setFormDuration(1);
      setFormMaxUses(1); setFormExpiresAt('');
      setSuccessMsg('Promo code created successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
      await fetchCodes();
    } catch (e: any) {
      setFormError(e.message || 'Failed to create promo code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (code: PromoCode) => {
    setTogglingId(code.id);
    try {
      const res = await fetch(`${API_BASE}/dashboard/promo-codes/${code.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-dashboard-secret': DASHBOARD_SECRET ?? '',
        },
        body: JSON.stringify({ isActive: !code.is_active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchCodes();
    } catch (e: any) {
      setError(e.message || 'Failed to update promo code');
    } finally {
      setTogglingId(null);
    }
  };

  const handleCopyUrl = async (code: PromoCode) => {
    try {
      await navigator.clipboard.writeText(`https://notely.se/code/${code.code}`);
      setCopiedUrlId(code.id);
      setTimeout(() => setCopiedUrlId(null), 2000);
    } catch { /* ignore */ }
  };

  const handleCopy = async (code: PromoCode) => {
    try {
      await navigator.clipboard.writeText(code.code);
      setCopiedId(code.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* ignore */ }
  };

  // ── Campaigns API ────────────────────────────────────────────────────────

  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const res = await fetch(`${API_BASE}/dashboard/campaigns`, {
        headers: { 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCampaigns(data.campaigns ?? []);
    } catch (e: any) {
      setCampaignsError(e.message || 'Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'campaigns') fetchCampaigns();
  }, [activeTab, fetchCampaigns]);

  const openCodesModal = async (campaign: Campaign) => {
    setCodesCampaign(campaign);
    setModalCodes([]);
    setModalCodesError(null);
    setModalCodesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/campaigns/${campaign.id}/codes`, {
        headers: { 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setModalCodes(data.codes ?? []);
    } catch (e: any) {
      setModalCodesError(e.message || 'Failed to load codes');
    } finally {
      setModalCodesLoading(false);
    }
  };

  const handleReactivateCode = async (codeId: number) => {
    setReactivatingCodeId(codeId);
    try {
      const res = await fetch(`${API_BASE}/dashboard/campaigns/${codesCampaign!.id}/codes/${codeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
        body: JSON.stringify({ isClaimed: false }),
      });
      if (res.ok) {
        setModalCodes(prev => prev.map(c => c.id === codeId ? { ...c, is_claimed: 0, claimed_at: null } : c));
        fetchCampaigns();
      } else {
        setModalCodesError(`Failed to reactivate code (HTTP ${res.status})`);
      }
    } catch (e: any) {
      setModalCodesError(e.message || 'Failed to reactivate code');
    } finally {
      setReactivatingCodeId(null);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    setCampFormError(null);
    if (!campName.trim()) { setCampFormError('Campaign name is required'); return; }
    setCampSubmitting(true);
    try {
      const iosList = campIosCodes.split('\n').map(s => s.trim()).filter(Boolean);
      const androidList = campAndroidCodes.split('\n').map(s => s.trim()).filter(Boolean);
      const res = await fetch(`${API_BASE}/dashboard/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-dashboard-secret': DASHBOARD_SECRET ?? '',
        },
        body: JSON.stringify({
          name: campName.trim(),
          fallbackUrl: campFallbackUrl.trim() || 'https://notely.se/download',
          iosRedirectUrl: campIosRedirectUrl.trim() || null,
          androidRedirectUrl: campAndroidRedirectUrl.trim() || null,
          iosCodes: iosList,
          androidCodes: androidList,
          expiresAt: campExpiresAt || null,
          useLandingPage: campUseLandingPage,
          primaryButtonText: campPrimaryButtonText.trim() || null,
          secondaryButtonText: campSecondaryButtonText.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setCampName(''); setCampFallbackUrl('https://notely.se/download');
      setCampIosRedirectUrl('https://apps.apple.com/redeem?ctx=offercodes&id=6740462619&code={code}');
      setCampAndroidRedirectUrl('https://play.google.com/redeem?code={code}');
      setCampIosCodes(''); setCampAndroidCodes(''); setCampExpiresAt('');
      setCampUseLandingPage(true); setCampPrimaryButtonText(''); setCampSecondaryButtonText('');
      setCampaignsSuccess('Campaign created successfully');
      setTimeout(() => setCampaignsSuccess(null), 3000);
      await fetchCampaigns();
    } catch (e: any) {
      setCampFormError(e.message || 'Failed to create campaign');
    } finally {
      setCampSubmitting(false);
    }
  };

  const handleToggleCampaign = async (campaign: Campaign) => {
    setTogglingCampId(campaign.id);
    try {
      const res = await fetch(`${API_BASE}/dashboard/campaigns/${campaign.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-dashboard-secret': DASHBOARD_SECRET ?? '',
        },
        body: JSON.stringify({ isActive: !campaign.is_active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchCampaigns();
    } catch (e: any) {
      setCampaignsError(e.message || 'Failed to update campaign');
    } finally {
      setTogglingCampId(null);
    }
  };

  const handleDeleteCampaign = async (campaign: Campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"? This cannot be undone.`)) return;
    setDeletingCampId(campaign.id);
    try {
      const res = await fetch(`${API_BASE}/dashboard/campaigns/${campaign.id}`, {
        method: 'DELETE',
        headers: { 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
    } catch (e: any) {
      setCampaignsError(e.message || 'Failed to delete campaign');
    } finally {
      setDeletingCampId(null);
    }
  };

  const handleAddCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addCodesCampaign) return;
    setAddCodesError(null);
    setAddCodesSubmitting(true);
    try {
      const iosList = addIosCodes.split('\n').map(s => s.trim()).filter(Boolean);
      const androidList = addAndroidCodes.split('\n').map(s => s.trim()).filter(Boolean);
      const res = await fetch(`${API_BASE}/dashboard/campaigns/${addCodesCampaign.id}/codes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-dashboard-secret': DASHBOARD_SECRET ?? '',
        },
        body: JSON.stringify({
          iosCodes: iosList,
          androidCodes: androidList,
          expiresAt: addExpiresAt || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAddCodesCampaign(null);
      setAddIosCodes(''); setAddAndroidCodes(''); setAddExpiresAt('');
      setCampaignsSuccess('Codes added successfully');
      setTimeout(() => setCampaignsSuccess(null), 3000);
      await fetchCampaigns();
    } catch (e: any) {
      setAddCodesError(e.message || 'Failed to add codes');
    } finally {
      setAddCodesSubmitting(false);
    }
  };

  const handleCopyQrUrl = async (campaign: Campaign) => {
    try {
      await navigator.clipboard.writeText(`https://notely.se/c/${campaign.slug || campaign.id}`);
      setQrUrlCopied(true);
      setTimeout(() => setQrUrlCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const openStatsModal = async (campaign: Campaign) => {
    setStatsCampaign(campaign);
    setStatsLoading(true);
    setStatsError(null);
    setStatsTotals([]);
    setStatsRecent([]);
    try {
      const res = await fetch(`${API_BASE}/dashboard/campaigns/${campaign.id}/clicks`, {
        headers: { 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatsTotals(data.totals ?? []);
      setStatsRecent(data.recent ?? []);
    } catch (e: any) {
      setStatsError(e.message || 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const openEditModal = (campaign: Campaign) => {
    setEditCampaign(campaign);
    setEditSlug(campaign.slug ?? '');
    setEditFallbackUrl(campaign.fallback_url);
    setEditIosRedirectUrl(campaign.ios_redirect_url ?? '');
    setEditAndroidRedirectUrl(campaign.android_redirect_url ?? '');
    setEditUseLandingPage(campaign.use_landing_page ?? true);
    setEditPrimaryButtonText(campaign.primary_button_text ?? '');
    setEditSecondaryButtonText(campaign.secondary_button_text ?? '');
    setEditError(null);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editCampaign) return;
    setEditSubmitting(true);
    setEditError(null);
    try {
      const res = await fetch(`${API_BASE}/dashboard/campaigns/${editCampaign.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-dashboard-secret': DASHBOARD_SECRET ?? '' },
        body: JSON.stringify({
          slug: editSlug.trim() || null,
          fallbackUrl: editFallbackUrl.trim(),
          iosRedirectUrl: editIosRedirectUrl.trim() || null,
          androidRedirectUrl: editAndroidRedirectUrl.trim() || null,
          useLandingPage: editUseLandingPage,
          primaryButtonText: editPrimaryButtonText.trim() || null,
          secondaryButtonText: editSecondaryButtonText.trim() || null,
        }),
      });
      if (!res.ok) throw new Error('Update failed');
      setEditCampaign(null);
      await fetchCampaigns();
    } catch (e: any) {
      setEditError(e.message || 'Failed to update campaign');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDownloadQR = (campaign: Campaign) => {
    const identifier = campaign.slug || campaign.id;
    const hdQr = new QRCodeStyling({
      width: 1024,
      height: 1024,
      data: `https://notely.se/c/${identifier}`,
      dotsOptions: { type: 'rounded', color: '#000000' },
      cornersSquareOptions: { type: 'extra-rounded' },
      cornersDotOptions: { type: 'dot' },
      backgroundOptions: { color: '#ffffff' },
      qrOptions: { errorCorrectionLevel: 'H' },
    });
    hdQr.download({
      name: `campaign-qr-${identifier}`,
      extension: 'png',
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <a href="/dashboard-process-logs" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <Tag className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold">Promo Codes</h1>
          </div>
          <button
            onClick={activeTab === 'codes' ? fetchCodes : fetchCampaigns}
            disabled={activeTab === 'codes' ? loading : campaignsLoading}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${(activeTab === 'codes' ? loading : campaignsLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 bg-gray-800 border border-gray-700 rounded-lg p-1 mb-6 w-fit">
          <button
            onClick={() => { setActiveTab('codes'); localStorage.setItem('promoCodes_activeTab', 'codes'); }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'codes' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            Codes
          </button>
          <button
            onClick={() => { setActiveTab('campaigns'); localStorage.setItem('promoCodes_activeTab', 'campaigns'); }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'campaigns' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            Campaigns
          </button>
        </div>

        {/* ── CODES TAB ─────────────────────────────────────────────────────── */}
        {activeTab === 'codes' && (
          <>
            {error && (
              <Alert className="mb-4 border-red-500 bg-red-900/20">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-300">{error}</AlertDescription>
              </Alert>
            )}
            {successMsg && (
              <Alert className="mb-4 border-green-500 bg-green-900/20">
                <Check className="h-4 w-4 text-green-400" />
                <AlertDescription className="text-green-300">{successMsg}</AlertDescription>
              </Alert>
            )}

            {/* Create Form */}
            <Card className="bg-gray-800 border-gray-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-lg">Create Promo Code</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-1">
                    <label className="block text-sm text-gray-400 mb-1">Code</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formCode}
                        onChange={e => setFormCode(e.target.value.toUpperCase())}
                        placeholder="E.g. PROMO2026"
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                      <button
                        type="button"
                        onClick={() => setFormCode(generateRandomCode())}
                        className="bg-gray-600 hover:bg-gray-500 px-3 py-2 rounded-lg text-xs transition-colors whitespace-nowrap"
                      >
                        Random
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Plan</label>
                    <select
                      value={formPlan}
                      onChange={e => setFormPlan(e.target.value as any)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    >
                      <option value="premium_monthly_1">Basic</option>
                      <option value="premium_monthly_2">Standard</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Duration (months)</label>
                    <input
                      type="number" min={1} value={formDuration}
                      onChange={e => setFormDuration(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Max uses</label>
                    <input
                      type="number" min={1} value={formMaxUses}
                      onChange={e => setFormMaxUses(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Expires at (optional)</label>
                    <input
                      type="date" value={formExpiresAt}
                      onChange={e => setFormExpiresAt(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit" disabled={submitting}
                      className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      {submitting ? 'Creating…' : 'Create Code'}
                    </button>
                  </div>
                  {formError && (
                    <div className="sm:col-span-2 lg:col-span-3 text-red-400 text-sm">{formError}</div>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Codes Table */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">
                  All Codes {!loading && <span className="text-gray-400 font-normal text-sm">({codes.length})</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : codes.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No promo codes yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-3 px-2">Code</th>
                          <th className="text-left py-3 px-2">Plan</th>
                          <th className="text-left py-3 px-2">Duration</th>
                          <th className="text-left py-3 px-2">Used / Max</th>
                          <th className="text-left py-3 px-2">Expires</th>
                          <th className="text-left py-3 px-2">Created</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {codes.map(code => (
                          <tr key={code.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-medium">{code.code}</span>
                                <button onClick={() => handleCopy(code)} className="text-gray-500 hover:text-gray-300 transition-colors" title="Copy to clipboard">
                                  {copiedId === code.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                                <button onClick={() => handleCopyUrl(code)} className="text-gray-500 hover:text-gray-300 transition-colors" title="Copy deep link URL">
                                  {copiedUrlId === code.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Link className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-gray-300">{PLAN_LABELS[code.plan] ?? code.plan}</td>
                            <td className="py-3 px-2 text-gray-300">{code.duration_months}mo</td>
                            <td className="py-3 px-2 text-gray-300">{code.used_count} / {code.max_uses}</td>
                            <td className="py-3 px-2 text-gray-300">
                              {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="py-3 px-2 text-gray-400 text-xs">
                              {new Date(code.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-2">
                              {code.is_active ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-400 border border-green-700/50">Active</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700/50 text-gray-400 border border-gray-600/50">Inactive</span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <button
                                onClick={() => handleToggle(code)}
                                disabled={togglingId === code.id}
                                className={`text-xs px-3 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                                  code.is_active
                                    ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-700/40'
                                    : 'bg-green-900/30 hover:bg-green-900/50 text-green-400 border border-green-700/40'
                                }`}
                              >
                                {togglingId === code.id ? '…' : code.is_active ? 'Deactivate' : 'Reactivate'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ── CAMPAIGNS TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'campaigns' && (
          <>
            {campaignsError && (
              <Alert className="mb-4 border-red-500 bg-red-900/20">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-300">{campaignsError}</AlertDescription>
              </Alert>
            )}
            {campaignsSuccess && (
              <Alert className="mb-4 border-green-500 bg-green-900/20">
                <Check className="h-4 w-4 text-green-400" />
                <AlertDescription className="text-green-300">{campaignsSuccess}</AlertDescription>
              </Alert>
            )}

            {/* Create Campaign Form */}
            <Card className="bg-gray-800 border-gray-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-lg">Create Campaign</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCampaign} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Campaign name</label>
                      <input
                        type="text"
                        value={campName}
                        onChange={e => setCampName(e.target.value)}
                        placeholder="E.g. Launch Week Jan 2026"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Fallback URL</label>
                      <input
                        type="text"
                        value={campFallbackUrl}
                        onChange={e => setCampFallbackUrl(e.target.value)}
                        placeholder="https://notely.se/download"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">iOS Redirect URL <span className="text-gray-500">(optional)</span></label>
                      <input
                        type="text"
                        value={campIosRedirectUrl}
                        onChange={e => setCampIosRedirectUrl(e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Android Redirect URL <span className="text-gray-500">(optional)</span></label>
                      <input
                        type="text"
                        value={campAndroidRedirectUrl}
                        onChange={e => setCampAndroidRedirectUrl(e.target.value)}

                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">
                        iOS codes <span className="text-gray-500">(one per line)</span>
                      </label>
                      <textarea
                        value={campIosCodes}
                        onChange={e => setCampIosCodes(e.target.value)}
                        placeholder={"APPSTORE-CODE-1\nAPPSTORE-CODE-2\nAPPSTORE-CODE-3"}
                        rows={5}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-purple-500 resize-y"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">
                        Android codes <span className="text-gray-500">(one per line)</span>
                      </label>
                      <textarea
                        value={campAndroidCodes}
                        onChange={e => setCampAndroidCodes(e.target.value)}
                        placeholder={"GPLAY-CODE-1\nGPLAY-CODE-2\nGPLAY-CODE-3"}
                        rows={5}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-purple-500 resize-y"
                      />
                    </div>
                  </div>

                  <div className="flex items-end gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Valid until (optional)</label>
                      <input
                        type="date"
                        value={campExpiresAt}
                        onChange={e => setCampExpiresAt(e.target.value)}
                        className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>

                  <div className="border-t border-gray-700 pt-4 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={campUseLandingPage}
                        onChange={e => setCampUseLandingPage(e.target.checked)}
                        className="w-4 h-4 accent-purple-500"
                      />
                      <span className="text-sm text-gray-300">Show landing page (FAQ, video &amp; download CTAs)</span>
                    </label>
                    {campUseLandingPage && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Primary button text <span className="text-gray-500">(optional)</span></label>
                          <input
                            type="text"
                            value={campPrimaryButtonText}
                            onChange={e => setCampPrimaryButtonText(e.target.value)}
                            placeholder="Download the app"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Secondary button text <span className="text-gray-500">(optional)</span></label>
                          <input
                            type="text"
                            value={campSecondaryButtonText}
                            onChange={e => setCampSecondaryButtonText(e.target.value)}
                            placeholder="Use the promotion code"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={campSubmitting}
                      className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      {campSubmitting ? 'Creating…' : 'Create Campaign'}
                    </button>
                  </div>

                  {campFormError && (
                    <p className="text-red-400 text-sm">{campFormError}</p>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Campaigns Table */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">
                  All Campaigns {!campaignsLoading && <span className="text-gray-400 font-normal text-sm">({campaigns.length})</span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {campaignsLoading ? (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : campaigns.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No campaigns yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-3 px-2">Name</th>
                          <th className="text-left py-3 px-2">iOS</th>
                          <th className="text-left py-3 px-2">Android</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">QR</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map(campaign => (
                          <React.Fragment key={campaign.id}>
                          <tr className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1.5">
                                <div className="font-medium text-white">{campaign.name}</div>
                                <button
                                  onClick={() => toggleCampExpanded(campaign.id)}
                                  className="text-gray-500 hover:text-gray-300 transition-colors"
                                  title="Show more details"
                                >
                                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedCampIds.has(campaign.id) ? 'rotate-180' : ''}`} />
                                </button>
                              </div>
                              {campaign.slug && (
                                <div className="text-xs text-purple-400 font-mono mt-0.5">/c/{campaign.slug}</div>
                              )}
                              <div className="text-xs text-gray-500 mt-0.5">
                                {new Date(campaign.created_at).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="text-gray-300 text-xs mb-1">
                                {campaign.ios_claimed} / {campaign.ios_total}
                              </div>
                              {campaign.ios_total > 0 && (
                                <div className="w-24 bg-gray-700 rounded-full h-1.5">
                                  <div
                                    className="bg-blue-500 h-1.5 rounded-full"
                                    style={{ width: `${Math.round((campaign.ios_claimed / campaign.ios_total) * 100)}%` }}
                                  />
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="text-gray-300 text-xs mb-1">
                                {campaign.android_claimed} / {campaign.android_total}
                              </div>
                              {campaign.android_total > 0 && (
                                <div className="w-24 bg-gray-700 rounded-full h-1.5">
                                  <div
                                    className="bg-green-500 h-1.5 rounded-full"
                                    style={{ width: `${Math.round((campaign.android_claimed / campaign.android_total) * 100)}%` }}
                                  />
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              {campaign.is_active ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-400 border border-green-700/50">Active</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700/50 text-gray-400 border border-gray-600/50">Inactive</span>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <button
                                onClick={() => { setQrCampaign(campaign); setQrUrlCopied(false); }}
                                className="flex items-center gap-1.5 text-xs bg-gray-700 hover:bg-gray-600 px-2.5 py-1.5 rounded-lg transition-colors"
                                title="Show QR code"
                              >
                                <QrCode className="w-3.5 h-3.5" />
                                QR
                              </button>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => { setAddCodesCampaign(campaign); setAddIosCodes(''); setAddAndroidCodes(''); setAddExpiresAt(''); setAddCodesError(null); }}
                                  className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 px-2.5 py-1.5 rounded-lg transition-colors"
                                  title="Add more codes"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Add
                                </button>
                                <button
                                  onClick={() => openCodesModal(campaign)}
                                  className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 px-2.5 py-1.5 rounded-lg transition-colors"
                                  title="View codes"
                                >
                                  <List className="w-3.5 h-3.5" />
                                  Codes
                                </button>
                                <button
                                  onClick={() => openStatsModal(campaign)}
                                  className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 px-2.5 py-1.5 rounded-lg transition-colors"
                                  title="Click stats"
                                >
                                  <BarChart2 className="w-3.5 h-3.5" />
                                  Stats
                                </button>
                                <button
                                  onClick={() => openEditModal(campaign)}
                                  className="flex items-center gap-1 text-xs bg-gray-700 hover:bg-gray-600 px-2.5 py-1.5 rounded-lg transition-colors"
                                  title="Edit URLs & slug"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleToggleCampaign(campaign)}
                                  disabled={togglingCampId === campaign.id}
                                  className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                                    campaign.is_active
                                      ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-700/40'
                                      : 'bg-green-900/30 hover:bg-green-900/50 text-green-400 border border-green-700/40'
                                  }`}
                                >
                                  {togglingCampId === campaign.id ? '…' : campaign.is_active ? 'Deactivate' : 'Reactivate'}
                                </button>
                                <button
                                  onClick={() => handleDeleteCampaign(campaign)}
                                  disabled={deletingCampId === campaign.id}
                                  className="text-xs bg-red-900/20 hover:bg-red-900/40 text-red-400 border border-red-700/30 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                                  title="Delete campaign"
                                >
                                  {deletingCampId === campaign.id ? '…' : <Trash2 className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedCampIds.has(campaign.id) && (
                            <tr className="border-b border-gray-700/50 bg-gray-800/40">
                              <td colSpan={6} className="px-4 py-3">
                                <div className="text-sm">
                                  <span className="text-gray-500">Valid until:</span>{' '}
                                  <span className="text-gray-300">{getCampaignValidUntil(campaign)}</span>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* ── QR Modal ────────────────────────────────────────────────────────── */}
      {qrCampaign && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setQrCampaign(null)}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-xs w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">QR Code — {qrCampaign.name}</h3>
              <button onClick={() => setQrCampaign(null)} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex justify-center mb-5">
              <div className="bg-white rounded-[28px] p-2 shadow-2xl">
                <div ref={qrContainerRef} />
              </div>
            </div>

            <div className="bg-gray-900/80 rounded-lg px-3 py-2 mb-4 border border-gray-700/50">
              <p className="text-gray-400 text-xs font-mono text-center break-all">
                https://notely.se/c/{qrCampaign.slug || qrCampaign.id}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleCopyQrUrl(qrCampaign)}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm transition-colors"
              >
                {qrUrlCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                {qrUrlCopied ? 'Copied!' : 'Copy URL'}
              </button>
              <button
                onClick={() => handleDownloadQR(qrCampaign)}
                className="flex-1 flex items-center justify-center gap-2 bg-purple-700 hover:bg-purple-600 px-3 py-2 rounded-lg text-sm transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PNG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Codes Modal ──────────────────────────────────────────────────── */}
      {/* ── Codes Modal ──────────────────────────────────────────────────────── */}
      {codesCampaign && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setCodesCampaign(null); setModalCodesError(null); }}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Codes — {codesCampaign.name}</h3>
              <button onClick={() => { setCodesCampaign(null); setModalCodesError(null); }} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {modalCodesLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : modalCodes.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No codes for this campaign.</p>
            ) : (
              <div className="overflow-y-auto flex-1">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-800">
                    <tr className="text-gray-400 border-b border-gray-700">
                      <th className="text-left py-2 px-2">Code</th>
                      <th className="text-left py-2 px-2">Platform</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-left py-2 px-2">Claimed at</th>
                      <th className="text-left py-2 px-2">Expires</th>
                      <th className="text-left py-2 px-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalCodes.map(code => (
                      <tr key={code.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                        <td className="py-2 px-2 font-mono text-xs text-gray-200">{code.code}</td>
                        <td className="py-2 px-2 text-xs text-gray-400 capitalize">{code.platform}</td>
                        <td className="py-2 px-2">
                          {code.is_claimed === 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-400 border border-green-700/50">Available</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/40 text-amber-400 border border-amber-700/50">Used</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-400">
                          {code.claimed_at ? new Date(code.claimed_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-400">
                          {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-2 px-2">
                          {code.is_claimed === 1 && (
                            <button
                              onClick={() => handleReactivateCode(code.id)}
                              disabled={reactivatingCodeId === code.id}
                              className="text-xs bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 border border-blue-700/40 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {reactivatingCodeId === code.id ? '…' : 'Reactivate'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {modalCodesError && (
              <p className="text-red-400 text-sm mt-3">{modalCodesError}</p>
            )}
          </div>
        </div>
      )}

      {/* ── Stats Modal ──────────────────────────────────────────────────────── */}
      {statsCampaign && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setStatsCampaign(null)}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Click Stats — {statsCampaign.name}</h3>
              <button onClick={() => setStatsCampaign(null)} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {statsLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : statsError ? (
              <p className="text-red-400 text-center py-8">{statsError}</p>
            ) : statsTotals.length === 0 && statsRecent.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No clicks yet.</p>
            ) : (
              <div className="overflow-y-auto flex-1 space-y-4">
                {statsTotals.length > 0 && (
                  <div>
                    <h4 className="text-gray-400 text-xs uppercase tracking-wide mb-2">By Platform</h4>
                    <div className="flex flex-wrap gap-3">
                      {statsTotals.map(t => (
                        <div key={t.platform} className="bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 min-w-[120px]">
                          <div className="text-gray-400 text-xs capitalize mb-1">{t.platform}</div>
                          <div className="text-white text-xl font-bold">{t.total}</div>
                          <div className="text-green-400 text-xs mt-1">{t.new_claims} new claims</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {statsRecent.length > 0 && (
                  <div>
                    <h4 className="text-gray-400 text-xs uppercase tracking-wide mb-2">Recent Clicks</h4>
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-800">
                        <tr className="text-gray-400 border-b border-gray-700">
                          <th className="text-left py-2 px-2">Time</th>
                          <th className="text-left py-2 px-2">Platform</th>
                          <th className="text-left py-2 px-2">Browser</th>
                          <th className="text-left py-2 px-2">IP</th>
                          <th className="text-left py-2 px-2">New?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsRecent.map(click => (
                          <tr key={click.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                            <td className="py-2 px-2 text-xs text-gray-400">
                              {new Date(click.clicked_at).toLocaleString()}
                            </td>
                            <td className="py-2 px-2 text-xs text-gray-300 capitalize">{click.platform}</td>
                            <td className="py-2 px-2 text-xs text-gray-300">{parseBrowserFromUA(click.user_agent)}</td>
                            <td className="py-2 px-2 text-xs text-gray-400 font-mono">{click.ip_address ?? '—'}</td>
                            <td className="py-2 px-2">
                              {click.is_new_claim === 1 ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-900/40 text-green-400">Yes</span>
                              ) : (
                                <span className="text-gray-600 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────────────── */}
      {editCampaign && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setEditCampaign(null)}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-lg w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Edit — {editCampaign.name}</h3>
              <button onClick={() => setEditCampaign(null)} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Custom URL slug</label>
                <div className="flex items-center gap-0">
                  <span className="bg-gray-900 border border-r-0 border-gray-600 rounded-l-lg px-3 py-2 text-gray-500 text-sm select-none">notely.se/c/</span>
                  <input
                    type="text"
                    value={editSlug}
                    onChange={e => setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
                    placeholder="summer-promo"
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-r-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Leave blank to use the auto-generated ID. Old ID always works as fallback.</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Fallback URL <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={editFallbackUrl}
                  onChange={e => setEditFallbackUrl(e.target.value)}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">iOS Redirect URL <span className="text-gray-500">(optional)</span></label>
                <input
                  type="text"
                  value={editIosRedirectUrl}
                  onChange={e => setEditIosRedirectUrl(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Android Redirect URL <span className="text-gray-500">(optional)</span></label>
                <input
                  type="text"
                  value={editAndroidRedirectUrl}
                  onChange={e => setEditAndroidRedirectUrl(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>

              <div className="border-t border-gray-700 pt-3 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={editUseLandingPage}
                    onChange={e => setEditUseLandingPage(e.target.checked)}
                    className="w-4 h-4 accent-purple-500"
                  />
                  <span className="text-sm text-gray-300">Show landing page</span>
                </label>
                {editUseLandingPage && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Primary button text <span className="text-gray-500">(optional)</span></label>
                      <input
                        type="text"
                        value={editPrimaryButtonText}
                        onChange={e => setEditPrimaryButtonText(e.target.value)}
                        placeholder="Download the app"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Secondary button text <span className="text-gray-500">(optional)</span></label>
                      <input
                        type="text"
                        value={editSecondaryButtonText}
                        onChange={e => setEditSecondaryButtonText(e.target.value)}
                        placeholder="Use the promotion code"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </>
                )}
              </div>

              {editError && <p className="text-red-400 text-sm">{editError}</p>}

              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setEditCampaign(null)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting}
                  className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 px-5 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {editSubmitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addCodesCampaign && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setAddCodesCampaign(null)}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-lg w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Add Codes — {addCodesCampaign.name}</h3>
              <button onClick={() => setAddCodesCampaign(null)} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddCodes} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    iOS codes <span className="text-gray-500">(one per line)</span>
                  </label>
                  <textarea
                    value={addIosCodes}
                    onChange={e => setAddIosCodes(e.target.value)}
                    placeholder={"APPSTORE-CODE-1\nAPPSTORE-CODE-2"}
                    rows={6}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-purple-500 resize-y"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Android codes <span className="text-gray-500">(one per line)</span>
                  </label>
                  <textarea
                    value={addAndroidCodes}
                    onChange={e => setAddAndroidCodes(e.target.value)}
                    placeholder={"GPLAY-CODE-1\nGPLAY-CODE-2"}
                    rows={6}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-purple-500 resize-y"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Valid until (optional)</label>
                  <input
                    type="date"
                    value={addExpiresAt}
                    onChange={e => setAddExpiresAt(e.target.value)}
                    className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="flex-1 flex justify-end items-end">
                  <button
                    type="submit"
                    disabled={addCodesSubmitting}
                    className="bg-purple-600 hover:bg-purple-500 disabled:opacity-50 px-5 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    {addCodesSubmitting ? 'Adding…' : 'Add Codes'}
                  </button>
                </div>
              </div>

              {addCodesError && (
                <p className="text-red-400 text-sm">{addCodesError}</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodes;
